import React, { useMemo, useState } from 'react';
import { LongTermWorkflow } from './Components/LongTermWorkflow/LongTermWorkflow';
import { RetirementWorkflow } from './Components/RetirementWorkflow/RetirementWorkflow';
import { FinancialGoal } from './AutoAccountWorkflow.types';
import { WorkflowSelection } from './Components/WorkflowSelection/WorkflowSelection';
import { AutoAccountWorkflowResult } from '../AutoAccountWorkflowResult/AutoAccountWorkflowResult';
import { AutomatedAccountsDTO, useAutomatedAccountsQuery } from './api/useAutomatedAccountsQuery';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { FullPageLoading } from '../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Content, StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { RiskToleranceWorkflow } from '../../RiskToleranceWorkflow/RiskToleranceWorkflow';
import { CustomerType } from '../../ApexOPL/AccountOpening/AccountOpening.meta';

export const AutoAccountWorkflow = () => {
  useDocumentTitle('Automated Account - Workflow');

  const { isLoading, data } = useAutomatedAccountsQuery();

  const [key, setKey] = useState(Date.now());

  if (isLoading) {
    return (
      <Box mt={96}>
        <Content>
          <FullPageLoading text="Loading..." size="medium" />
        </Content>

        <StickyFooter />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <AutoAccountWorkflowContent key={key} data={data} restartWizard={() => setKey(Date.now())} />
  );
};

interface Props {
  data: AutomatedAccountsDTO;
  restartWizard(): void;
}

const AutoAccountWorkflowContent = ({ data, restartWizard }: Props) => {
  const initialActiveWorkflow = useMemo(() => {
    if (
      !(
        data.allowedConsumerTypes.includes(CustomerType.Individual) ||
        data.allowedConsumerTypes.includes(CustomerType.Joint)
      )
    ) {
      return FinancialGoal.Retirement;
    }

    if (!data.allowedConsumerTypes.includes(CustomerType.Ira)) {
      return FinancialGoal.LongTerm;
    }

    return null;
  }, [data.allowedConsumerTypes]);

  const [activeWorkflow, setActiveWorkflow] = useState<FinancialGoal | null>(initialActiveWorkflow);

  const [showResults, setShowResults] = useState(false);

  const onSuccess = () => {
    setActiveWorkflow(FinancialGoal.RiskTolerance);
  };

  if (showResults) {
    return <AutoAccountWorkflowResult restartWizard={restartWizard} />;
  }

  return (
    <>
      {activeWorkflow === null && <WorkflowSelection setActiveWorkflow={setActiveWorkflow} />}

      {activeWorkflow === FinancialGoal.LongTerm && (
        <LongTermWorkflow
          restartWizard={restartWizard}
          onSuccess={onSuccess}
          data={data}
          initialActiveWorkflow={initialActiveWorkflow}
        />
      )}

      {activeWorkflow === FinancialGoal.Retirement && (
        <RetirementWorkflow
          restartWizard={restartWizard}
          onSuccess={onSuccess}
          data={data}
          initialActiveWorkflow={initialActiveWorkflow}
        />
      )}

      {activeWorkflow === FinancialGoal.RiskTolerance && (
        <RiskToleranceWorkflow
          isAutoAccountsWorkflow
          onFinish={() => {
            setShowResults(true);
          }}
        />
      )}
    </>
  );
};
